// components/tokens/TokenDetail.js
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Web3 from 'web3';
import { contractABI, contractAddress } from '../../contractConfig';
import '../../loadStyle.css';

const TokenDetail = () => {
  const { tokenId } = useParams();
  const id = parseInt(tokenId, 10); // ✅ ensure number

  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [contractURI, setContractURI] = useState({});

  // ✅ Public RPC (no MetaMask required)
  const getContract = () => {
    const rpcURL = 'https://polygon-amoy.drpc.org'; // Polygon Amoy testnet
    const web3 = new Web3(new Web3.providers.HttpProvider(rpcURL));
    return new web3.eth.Contract(contractABI, contractAddress);
  };

  // ✅ Normalize all IPFS links to Pinata's public gateway
  const replaceIPFSLink = useCallback((url) => {
    if (!url) return '';

    url = url.trim();

    // Keep ordinary web URLs unchanged
    if (
      (url.startsWith('https://') || url.startsWith('http://')) &&
      !url.includes('/ipfs/')
    ) {
      return url;
    }

    // Extract the CID/path from an IPFS URI or gateway URL
    const ipfsPath = url.includes('/ipfs/')
      ? url.split('/ipfs/')[1]
      : url
          .replace(/^ipfs:\/\/ipfs\//i, '')
          .replace(/^ipfs:\/\//i, '');

    return `https://gateway.pinata.cloud/ipfs/${ipfsPath}`;
  }, []);

  // ✅ Fetch collection metadata
  const fetchContractURI = useCallback(async () => {
    const contract = getContract();
    try {
      const uri = await contract.methods.contractURI().call();
      const formattedURI = replaceIPFSLink(uri);

      const res = await fetch(formattedURI);
      if (!res.ok) throw new Error(`Failed to fetch contract metadata: ${res.statusText}`);

      const metadata = await res.json();
      setContractURI({
        ...metadata,
        image: replaceIPFSLink(metadata.image),
        external_link: metadata.external_link, // Keep as is for web URLs
      });
    } catch (err) {
      console.error(err);
      setError('Error fetching contract URI: ' + err.message);
    }
  }, [replaceIPFSLink]);

  // ✅ Fetch token metadata and verify content
  const fetchToken = useCallback(async () => {
    const contract = getContract();
    try {
      const tokenURI = await contract.methods.tokenURI(id).call();
      const formattedURI = replaceIPFSLink(tokenURI);

      const res = await fetch(formattedURI);
      if (!res.ok) throw new Error(`Failed to fetch token metadata: ${res.statusText}`);

      const metadata = await res.json();
      metadata.image = replaceIPFSLink(metadata.image);

      let approvalCount = 0;
      try {
        const request = await contract.methods.mintRequests(id).call();
        approvalCount = parseInt(request.approvalCount, 10);
      } catch {
        console.warn(`No mintRequests entry for token ${id}`);
      }

      setToken({ tokenId: id, approvalCount, ...metadata });

      // ✅ Verification using Pinata's public gateway
      try {
        const ipfsPath = formattedURI.includes('/ipfs/')
          ? formattedURI.split('/ipfs/')[1]
          : formattedURI
              .replace(/^ipfs:\/\/ipfs\//i, '')
              .replace(/^ipfs:\/\//i, '');

        const verificationUrl =
          `https://gateway.pinata.cloud/ipfs/${ipfsPath}`;

        const verifyRes = await fetch(verificationUrl);

        if (!verifyRes.ok) {
          throw new Error(
            `Verification request failed: ${verifyRes.status}`
          );
        }

        const pinataData = await verifyRes.json();

        const normalize = (value) => {
          if (!value) return '';

          return value
            .toString()
            .trim()
            .replace(/^ipfs:\/\/ipfs\//i, '')
            .replace(/^ipfs:\/\//i, '')
            .replace(/^https?:\/\/[^/]+\/ipfs\//i, '')
            .toLowerCase();
        };

        const nameEqual =
          (metadata.name || '').trim().toLowerCase() ===
          (pinataData.name || '').trim().toLowerCase();

        const descEqual =
          (metadata.description || '').trim().toLowerCase() ===
          (pinataData.description || '').trim().toLowerCase();

        const imgEqual =
          normalize(metadata.image) ===
          normalize(pinataData.image);

        const equal =
          nameEqual &&
          descEqual &&
          imgEqual;

        setIsVerified(equal);

        console.log(
          equal
            ? '✅ Metadata successfully retrieved and matched'
            : '⚠️ Metadata comparison failed'
        );
      } catch (verifyErr) {
        setIsVerified(false);

        console.warn(
          'Verification failed:',
          verifyErr.message
        );
      }
    } catch (err) {
      console.error(err);
      setError('Error fetching token metadata: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [id, replaceIPFSLink]);

  // ✅ Run on mount
  useEffect(() => {
    fetchContractURI();
    fetchToken();
  }, [fetchContractURI, fetchToken]);

  if (loading) return <p>Loading token...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="token-detail-page">
      {/* ===== Collection Info ===== */}
      {contractURI && (
        <div className="collection-container">
          <section className="collection-item">
            <h3>Digital Folder Profile:</h3>
            <p><strong>Name:</strong> {contractURI.name}</p>
            <p><strong>Description:</strong> {contractURI.description}</p>

            {contractURI.image && (
              <div>
                <strong>Image:</strong>
                <img
                  className="collection-image"
                  src={contractURI.image}
                  alt={contractURI.name}
                />
              </div>
            )}

            {contractURI.external_link && (
              <p>
                <strong>External Link:</strong>{' '}
                <a
                  href={contractURI.external_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {contractURI.external_link}
                </a>
              </p>
            )}

            <p>
              <strong>Proof of Integrity:</strong>{' '}
              {isVerified ? '✅ Verified on Blockchain' : '❌ Verification Failed'}
            </p>
          </section>
        </div>
      )}

      {/* ===== Token Details ===== */}
      <div className="token-container">
        <section className="token-item">
          <h3>Licence Details</h3>
          {token ? (
            <>
              <p><strong>Licence ID:</strong> {token.tokenId}</p>
              <p><strong>Multisig Approval:</strong> {token.approvalCount} Signers</p>
              <p><strong>Name:</strong> {token.name}</p>
              <p className="description"><strong>Description:</strong> {token.description}</p>

              {token.image && (
                <div>
                  <strong>Image:</strong>
                  <img className="token-image" src={token.image} alt={token.name} />
                </div>
              )}

              <p>
                <strong>Proof of Integrity:</strong>{' '}
                {isVerified ? '✅ Verified on Blockchain' : '❌ Verification Failed'}
              </p>
            </>
          ) : (
            <p>No token data available.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default TokenDetail;